import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { BookOpen, X } from "lucide-react";
import { useGlossary } from "@/lib/glossary";
import { useT } from "@/lib/i18n";

/**
 * One-screen Legend & glossary (CLAR-08) — every recurring term, the sign
 * convention, and the severity order in one self-service card. Opened from
 * the command palette ("Legend & glossary") via the `ems:glossary` window
 * event so the palette doesn't need a prop channel through App.
 *
 * Wrapped in Radix Dialog (XTRA-04): role/aria-modal, focus trap, focus
 * restore and Esc-to-close come from the primitive — same idiom as
 * alarms/TemplatesGallery. Content nested inside Overlay (documented Radix
 * pattern) keeps the original flex-centering classes unchanged.
 *
 * Localized: terms/definitions via useGlossary(), chrome via useT() —
 * both re-render here when the operator switches language.
 */
export function GlossaryDialog() {
  const [open, setOpen] = useState(false);
  const t = useT();
  const glossary = useGlossary();

  useEffect(() => {
    function onOpen() { setOpen(true); }
    window.addEventListener("ems:glossary", onOpen);
    return () => window.removeEventListener("ems:glossary", onOpen);
  }, []);

  const entries = Object.values(glossary);
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[80] flex items-center justify-center bg-bg/70 px-4 backdrop-blur-sm">
          <Dialog.Content
            aria-describedby={undefined}
            className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-edge bg-surface shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-edge px-4 py-3">
              <Dialog.Title className="flex items-center gap-2 text-sm font-semibold">
                <BookOpen size={14} /> {t("glossary.title")}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button className="rounded p-1 text-text-faint transition hover:text-text" aria-label={t("common.close")}>
                  <X size={14} />
                </button>
              </Dialog.Close>
            </div>
            <div className="overflow-y-auto">
              <ul className="grid grid-cols-1 divide-y divide-edge md:grid-cols-2 md:divide-x">
                {entries.map((e) => (
                  <li key={e.term} className="px-4 py-2.5">
                    <div className="text-xs font-semibold text-text">{e.term}</div>
                    <div className="mt-0.5 text-[11px] leading-relaxed text-text-muted">{e.def}</div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-edge bg-surface-2 px-4 py-2 text-[10px] text-text-muted">
              {t("glossary.colors")} <span className="text-import font-semibold">{t("glossary.color.import")}</span> ·{" "}
              <span className="text-export font-semibold">{t("glossary.color.export")}</span> ·{" "}
              <span className="text-charge font-semibold">{t("glossary.color.charge")}</span> ·{" "}
              <span className="text-discharge font-semibold">{t("glossary.color.discharge")}</span> ·{" "}
              <span className="text-solar font-semibold">{t("glossary.color.solar")}</span>{" "}
              {t("glossary.colorNote")}
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
